import type { PageDefinition } from '@/types/page'
import { FEEDBACK_STATUS, TICKET_PRIORITY, TICKET_STATUS, toOptions } from './common'

const TICKET_TYPES = ['用药咨询', '设备故障', '会员支付', '健康报告', '账号问题'].map(value => ({ value, label: value }))
const FEEDBACK_TYPES = ['功能建议', '内容纠错', '服务投诉', '体验问题'].map(value => ({ value, label: value }))
const SOURCE_PAGES = ['首页', '用药计划', '健康报告', '个人中心', '帮助中心'].map(value => ({ value, label: value }))

export const supportTicketsPage: PageDefinition = {
  id: 'support-tickets',
  route: '#/support/tickets',
  title: '客服工单',
  purpose: '受理用药、设备、账号、报告与会员支付问题，跟踪服务 SLA、指派、处理与闭环',
  group: '客服与反馈',
  icon: 'ticket',
  kind: 'table',
  permission: 'support.view',
  permissions: { handle: 'support.handle', assign: 'support.assign', close: 'support.close' },
  searchKeys: ['ticket_no', 'title', 'user_name', 'family_name', 'type', 'assignee_name'],
  statusKey: 'status',
  statusMap: TICKET_STATUS,
  timeField: 'created_at',
  summary: [
    { key: 'total', label: '工单总数', kind: 'count' },
    { key: 'pending', label: '待受理', kind: 'count', where: { status: 'pending' } },
    { key: 'processing', label: '处理中', kind: 'count', where: { status: 'processing' } },
    { key: 'urgent', label: '紧急工单', kind: 'count', where: { priority: '紧急' } },
    { key: 'resolved', label: '已解决', kind: 'count', where: { status: 'resolved' } },
  ],
  columns: [
    { key: 'ticket_no', label: '工单编号', type: 'primary', subtitleKey: 'title', width: 200, sortable: true },
    { key: 'user_name', label: '用户', minWidth: 100, subtitleKey: 'family_name' },
    { key: 'type', label: '问题类型', width: 100 },
    { key: 'priority', label: '优先级', type: 'status', width: 84, options: toOptions(TICKET_PRIORITY) },
    { key: 'status', label: '状态', type: 'status', width: 90 },
    { key: 'assignee_name', label: '处理人', width: 90 },
    { key: 'channel', label: '来源', minWidth: 130 },
    { key: 'sla_due_at', label: 'SLA 截止', type: 'datetime', width: 150 },
    { key: 'created_at', label: '创建时间', type: 'datetime', width: 150, sortable: true },
  ],
  filters: [
    { key: 'keyword', label: '关键词', type: 'keyword', placeholder: '工单编号、标题或用户', width: 230 },
    { key: 'type', label: '问题类型', type: 'select', options: TICKET_TYPES, width: 116 },
    { key: 'priority', label: '优先级', type: 'select', options: toOptions(TICKET_PRIORITY), width: 100 },
    { key: 'status', label: '工单状态', type: 'select', options: toOptions(TICKET_STATUS), width: 112 },
    { key: 'assignee_name', label: '处理人', type: 'select', options: ['周可欣', '顾星', '沈嘉禾'].map(value => ({ value, label: value })), width: 106 },
    { key: 'created_at', label: '创建时间', type: 'daterange', timeField: 'created_at', width: 230 },
  ],
  exportScope: 'none',
  rowActions: [
    { key: 'view', label: '详情' },
    {
      key: 'assign',
      label: '指派',
      permission: 'support.assign',
      tone: 'primary',
      form: [
        { key: 'assignee_name', label: '处理人', type: 'select', options: ['周可欣', '顾星', '沈嘉禾'].map(value => ({ value, label: value })), required: true },
        { key: 'note', label: '指派备注', type: 'textarea', full: true, maxLength: 120 },
      ],
      visibleWhen: { field: 'status', in: ['pending', 'assigned', 'processing'] },
    },
    { key: 'reply', label: '回复用户', permission: 'support.handle', form: [{ key: 'reply', label: '回复内容', type: 'textarea', required: true, full: true, maxLength: 300 }], visibleWhen: { field: 'status', in: ['pending', 'assigned', 'processing'] } },
    { key: 'priority', label: '调整优先级', permission: 'support.assign', form: [{ key: 'priority', label: '优先级', type: 'select', options: toOptions(TICKET_PRIORITY), required: true }] },
    { key: 'escalate', label: '升级工单', permission: 'support.assign', tone: 'danger', confirm: true, confirmText: '升级后工单标记为紧急并进入客服主管队列，请确认已评估用户影响。', visibleWhen: { field: 'priority', in: ['低', '中', '高'] } },
    { key: 'handle', label: '标记已解决', permission: 'support.handle', visibleWhen: { field: 'status', in: ['assigned', 'processing'] }, form: [{ key: 'resolution', label: '处理结果', type: 'textarea', required: true, full: true }] },
    { key: 'close', label: '关闭工单', permission: 'support.close', tone: 'danger', confirm: true, form: [{ key: 'close_reason', label: '关闭结论', type: 'textarea', required: true, full: true, maxLength: 200 }], visibleWhen: { field: 'status', in: ['resolved'] } },
  ],
  batchActions: [
    { key: 'batch-assign', label: '批量指派', permission: 'support.assign', kind: 'task', form: [{ key: 'assignee_name', label: '处理人', type: 'select', options: ['周可欣', '顾星', '沈嘉禾'].map(value => ({ value, label: value })), required: true }] },
    { key: 'batch-close', label: '批量关闭', permission: 'support.close', tone: 'danger', kind: 'transition', confirm: true, form: [{ key: 'reason', label: '关闭结论', type: 'textarea', required: true, full: true }] },
  ],
  detailSections: [
    { title: '工单信息', fields: ['ticket_no', 'title', 'user_name', 'family_name', 'type', 'priority', 'status', 'channel'] },
    { title: '处理过程', fields: ['assignee_name', 'priority', 'description', 'last_reply', 'replied_at', 'escalated_at', 'sla_due_at', 'created_at', 'updated_at'] },
  ],
  detailMetrics: [
    { label: '优先级', field: 'priority', type: 'status' },
    { label: '处理人', field: 'assignee_name', type: 'text' },
    { label: 'SLA 截止', field: 'sla_due_at', type: 'datetime' },
  ],
  notice: '紧急工单需在 SLA 内完成首响；关闭工单前必须确认用户问题已解决并保留沟通记录。',
  emptyHint: '当前筛选下没有客服工单。',
  seedKey: 'tickets',
}

export const supportFeedbackPage: PageDefinition = {
  id: 'support-feedback',
  route: '#/support/feedback',
  title: '用户反馈',
  purpose: '处理用户对功能、内容、服务与隐私的反馈，回复用户并按需转为客服工单',
  group: '客服与反馈',
  icon: 'feedback',
  kind: 'table',
  permission: 'support.view',
  permissions: { handle: 'support.handle', assign: 'support.assign', close: 'support.close' },
  searchKeys: ['feedback_no', 'content', 'user_name', 'type', 'source_page', 'ticket_no'],
  statusKey: 'status',
  statusMap: FEEDBACK_STATUS,
  timeField: 'created_at',
  summary: [
    { key: 'total', label: '反馈总数', kind: 'count' },
    { key: 'pending', label: '待处理', kind: 'count', where: { status: 'pending' } },
    { key: 'replied', label: '已回复', kind: 'count', where: { status: 'replied' } },
    { key: 'tickets', label: '转工单', kind: 'count', hint: '已关联客服工单' },
  ],
  columns: [
    { key: 'feedback_no', label: '反馈编号', type: 'primary', subtitleKey: 'content', width: 270, sortable: true },
    { key: 'user_name', label: '用户', width: 100 },
    { key: 'type', label: '反馈类型', width: 104 },
    { key: 'source_page', label: '来源页面', width: 104 },
    { key: 'status', label: '状态', type: 'status', width: 90 },
    { key: 'ticket_no', label: '关联工单', width: 120 },
    { key: 'created_at', label: '提交时间', type: 'datetime', width: 150, sortable: true },
  ],
  filters: [
    { key: 'keyword', label: '关键词', type: 'keyword', placeholder: '反馈编号、内容或用户', width: 230 },
    { key: 'type', label: '反馈类型', type: 'select', options: FEEDBACK_TYPES, width: 112 },
    { key: 'status', label: '处理状态', type: 'select', options: toOptions(FEEDBACK_STATUS), width: 112 },
    { key: 'source_page', label: '来源页面', type: 'select', options: SOURCE_PAGES, width: 112 },
    { key: 'created_at', label: '提交时间', type: 'daterange', timeField: 'created_at', width: 230 },
  ],
  exportPermission: 'support.view',
  exportScope: 'filtered',
  rowActions: [
    { key: 'view', label: '详情' },
    {
      key: 'reply',
      label: '回复',
      permission: 'support.handle',
      tone: 'primary',
      form: [{ key: 'reply', label: '回复内容', type: 'textarea', required: true, full: true, maxLength: 300 }],
      visibleWhen: { field: 'status', in: ['pending', 'processing'] },
    },
    { key: 'create-ticket', label: '转工单', permission: 'support.assign', confirm: true, visibleWhen: { field: 'status', in: ['pending', 'processing'] } },
    { key: 'close', label: '关闭', permission: 'support.close', tone: 'danger', confirm: true, form: [{ key: 'close_reason', label: '关闭结论', type: 'textarea', required: true, full: true, maxLength: 200 }], visibleWhen: { field: 'status', in: ['replied'] } },
  ],
  detailSections: [
    { title: '反馈内容', fields: ['feedback_no', 'user_name', 'type', 'source_page', 'content', 'tags'] },
    { title: '处理信息', fields: ['status', 'reply', 'ticket_no', 'created_at', 'closed_at', 'updated_at'] },
  ],
  notice: '回复会通过站内信触达用户；转为工单后保留原反馈并关联工单编号，不重复创建。',
  emptyHint: '当前筛选下没有用户反馈。',
  seedKey: 'feedback',
}
